'use client'
import * as React from 'react'
import * as Dialog from 'primitives/dialog'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { Button } from '@repo/design-system-cf/components/button'
import { IconClose } from '@repo/design-system-cf/components/icons'
import { useDeleteDialog } from '../../contexts/delete-dialog-context'
import {
  useSocialDeleteCommentMutation,
  socialListCommentsQueryRootKey,
  useSocialDeletePostMutation,
  socialDiscoveryFeedQueryRootKey,
  socialUserFeedQueryRootKey,
  socialGetPostQueryRootKey,
  socialFollowersFeedQueryRootKey,
  socialSavedFeedQueryRootKey,
} from '@web/gen/feed'
import { useRouter } from 'next/navigation'
import { Z_INDEX_MODALS } from 'constants/modals'
import { useFeedRoute } from '../../contexts/feed-route-context'

export function DeleteDialog() {
  const t = useTranslations('social-feed.components.feed.post')
  const router = useRouter()
  const { basePath } = useFeedRoute()
  const { isOpen, closeDialog, targetId, variant, isPostPage, redirectTo, onDeleted } =
    useDeleteDialog()
  const socialDeleteCommentMutation = useSocialDeleteCommentMutation()
  const socialDeleteMutation = useSocialDeletePostMutation()
  const queryClient = useQueryClient()

  const isComment = variant === 'comment'

  const { mutate: deletePost } = useMutation({
    // oxlint-disable-next-line require-await
    mutationFn: async (id: string) => {
      return isComment
        ? socialDeleteCommentMutation.mutateAsync({ input: { commentId: id } })
        : socialDeleteMutation.mutateAsync({ input: { postId: id } })
    },
    // onSuccess(data, variables) => variables is the targetId in v5
    onSuccess: async (_data, deletedId) => {
      closeDialog()

      // Optimistically strip the deleted post from any infinite lists
      const removeFromInfinite = (rootKey: string) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        queryClient.setQueriesData({ queryKey: [rootKey], exact: false }, (old: any) => {
          if (!old || !old.pages) {
            return old
          }
          return {
            ...old,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            pages: old.pages.map((p: any) => ({
              ...p,
              items: Array.isArray(p.items)
                ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  p.items.filter((it: any) => it?.id !== deletedId)
                : p.items,
            })),
          }
        })
      }

      // oxlint-disable-next-line no-negated-condition
      if (!isComment) {
        removeFromInfinite(socialDiscoveryFeedQueryRootKey)
        removeFromInfinite(socialUserFeedQueryRootKey)

        await Promise.allSettled([
          queryClient.invalidateQueries({ queryKey: [`${socialUserFeedQueryRootKey}.Infinite`] }),
          queryClient.invalidateQueries({
            queryKey: [`${socialDiscoveryFeedQueryRootKey}.Infinite`],
          }),
          queryClient.invalidateQueries({
            queryKey: [`${socialFollowersFeedQueryRootKey}.Infinite`],
          }),
          queryClient.invalidateQueries({ queryKey: [`${socialSavedFeedQueryRootKey}.Infinite`] }),
          queryClient.invalidateQueries({ queryKey: [`${socialGetPostQueryRootKey}.Infinite`] }),
        ])
        await Promise.allSettled([
          queryClient.refetchQueries({ queryKey: [socialDiscoveryFeedQueryRootKey], exact: false }),
          queryClient.refetchQueries({ queryKey: [socialUserFeedQueryRootKey], exact: false }),
          queryClient.refetchQueries({ queryKey: [socialFollowersFeedQueryRootKey], exact: false }),
        ])

        if (isPostPage) {
          router.replace(redirectTo ?? basePath)
        }
      } else {
        // Optimistically update comment counts before refetching
        onDeleted?.()
        // Optimistically strip the deleted comment from all comment list caches
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        queryClient.setQueriesData(
          { queryKey: [socialListCommentsQueryRootKey], exact: false },
          (old: any) => {
            if (!old?.socialListComments?.comments) {
              return old
            }
            return {
              ...old,
              socialListComments: {
                ...old.socialListComments,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                comments: old.socialListComments.comments.filter((c: any) => c.id !== deletedId),
              },
            }
          },
        )
        // comments path — also best effort
        await Promise.allSettled([
          queryClient.refetchQueries({ queryKey: [socialListCommentsQueryRootKey], exact: false }),
          queryClient.refetchQueries({ queryKey: [socialGetPostQueryRootKey], exact: false }),
        ])
      }
    },
  })

  const handleDelete = () => {
    if (targetId) {
      deletePost(targetId)
    }
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={open => !open && closeDialog()} defaultOpen>
      <React.Suspense fallback={null}>
        <Dialog.Portal>
          <Dialog.Body
            className="font-poppins z-50 xl:overflow-y-auto xl:py-10"
            overlayZIndex={Z_INDEX_MODALS.default}
            zIndex={Z_INDEX_MODALS.default}
          >
            <div className="bg-cf-white rounded-4 relative w-full max-w-[500px] p-6">
              <h2 className="text-20 text-cf-sf-gray-1000 font-bold pb-3">
                {isComment ? t('delete_comment_dialog_title') : t('delete_post_dialog_title')}
              </h2>
              <p className="text-16 text-cf-sf-gray-100 pb-8">
                {isComment ? t('delete_comment_dialog_text') : t('delete_post_dialog_text')}
              </p>
              <div className="flex w-full justify-end gap-4">
                <Button variant="ghost" onClick={closeDialog}>
                  {t('cancel_button_text')}
                </Button>
                <Button onClick={handleDelete}>{t('delete_button_text')}</Button>
              </div>
              <Dialog.Close
                className="hover:bg-text-hover text-cf-purple-900 z-100 absolute right-5 top-4 flex h-6 w-6 items-center justify-center rounded-full font-bold"
                onClick={closeDialog}
              >
                <IconClose className="h-full w-full" />
              </Dialog.Close>
            </div>
          </Dialog.Body>
        </Dialog.Portal>
      </React.Suspense>
    </Dialog.Root>
  )
}

export function DeleteDialogMobile({
  isComment = false,
  targetId,
  closeDialog,
  onDeleted,
}: {
  isComment?: boolean
  targetId: string
  closeDialog: () => void
  onDeleted?: () => void
}) {
  const t = useTranslations('social-feed.components.feed.post')
  const socialDeleteCommentMutation = useSocialDeleteCommentMutation()
  const socialDeleteMutation = useSocialDeletePostMutation()
  const queryClient = useQueryClient()

  const { mutate: deletePost } = useMutation({
    // oxlint-disable-next-line require-await
    mutationFn: async (id: string) => {
      return isComment
        ? socialDeleteCommentMutation.mutateAsync({ input: { commentId: id } })
        : socialDeleteMutation.mutateAsync({ input: { postId: id } })
    },
    onSuccess: (_data, deletedId) => {
      closeDialog()
      const removeFromInfinite = (rootKey: string) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        queryClient.setQueriesData({ queryKey: [rootKey], exact: false }, (old: any) => {
          if (!old || !old.pages) {
            return old
          }
          return {
            ...old,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            pages: old.pages.map((p: any) => ({
              ...p,
              items: Array.isArray(p.items)
                ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  p.items.filter((it: any) => it?.id !== deletedId)
                : p.items,
            })),
          }
        })
      }

      // oxlint-disable-next-line no-negated-condition
      if (!isComment) {
        removeFromInfinite(socialDiscoveryFeedQueryRootKey)
        removeFromInfinite(socialUserFeedQueryRootKey)

        // Now mark them stale so active observers refetch
        queryClient.invalidateQueries({ queryKey: [socialDiscoveryFeedQueryRootKey], exact: false })
        queryClient.invalidateQueries({ queryKey: [socialUserFeedQueryRootKey], exact: false })
      } else {
        // Optimistically update comment counts before refetching
        onDeleted?.()
        // Optimistically strip the deleted comment from all comment list caches
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        queryClient.setQueriesData(
          { queryKey: [socialListCommentsQueryRootKey], exact: false },
          (old: any) => {
            if (!old?.socialListComments?.comments) {
              return old
            }
            return {
              ...old,
              socialListComments: {
                ...old.socialListComments,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                comments: old.socialListComments.comments.filter((c: any) => c.id !== deletedId),
              },
            }
          },
        )
        // Refetch comments list and all post queries to update comment counts
        queryClient.refetchQueries({ queryKey: [socialListCommentsQueryRootKey], exact: false })
        queryClient.refetchQueries({ queryKey: [socialGetPostQueryRootKey], exact: false })
      }
    },
  })

  const handleDelete = () => {
    if (targetId) {
      deletePost(targetId)
    }
  }

  return (
    <div className="px-2">
      <h2 className="text-16 text-cf-sf-gray-1000 font-semibold pb-3">
        {isComment ? t('delete_comment_dialog_title') : t('delete_post_dialog_title')}
      </h2>
      <p className="text-16 text-cf-sf-gray-100 pb-8">
        {isComment ? t('delete_comment_dialog_text') : t('delete_post_dialog_text')}
      </p>
      <div className="flex w-full justify-between gap-4">
        <Button variant="ghost" onClick={closeDialog} className="w-full">
          {t('cancel_button_text')}
        </Button>
        <Button onClick={handleDelete} className="w-full">
          {t('delete_button_text')}
        </Button>
      </div>
      <Dialog.Close
        className="hover:bg-text-hover text-cf-purple-900 z-100 absolute right-5 top-4 flex h-6 w-6 items-center justify-center rounded-full font-bold"
        onClick={closeDialog}
      >
        <IconClose className="h-full w-full" />
      </Dialog.Close>
    </div>
  )
}
